import {ChangeDetectionStrategy, Component, computed, DestroyRef, Injectable, input, OnDestroy, output, signal} from "@angular/core";
import {AutentiseringStatus, AutentisertOkModell, CriiptoAutentiseringsResponsModell, CriiptoGuiModus, CriiptoIdTilbyder, CriiptoOIDCRessursClient, CriiptoTokenInnloggingsSpesifikasjon, HttpsUrl, SubjectIdIkkeKnyttetTilBrukerModell, Tokenvarighet} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {alleEnuminnslag} from "felles-ng2/støttefunksjoner/enumer";
import {BildeRessurser} from "felles-ng2/tjenester/BildeRessurser";
import {MatButton} from "@angular/material/button";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {erOverlappendeLister} from "felles-ng2/støttefunksjoner/lister";
import {RessursAdresser} from "felles-ng2/tjenester/ressurser/RessursAdresser";
import {åpneINyttvindu} from "felles-ng2/støttefunksjoner/nettleser";
import {CriiptoAutentisertMelding, lyttTilCriiptoAutentisertHendelser} from "felles-ng2/støttefunksjoner/criipto";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {Brukerautentisering} from "felles-ng2/tjenester/Brukerautentisering";
import {TranslateModule} from "@ngx-translate/core";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {Varighet} from "felles/tjenester/MeldingTilBruker";

const debug = initDebugger('CriiptoAutentiseringPopupKomponent');

/**
 * Gui for valg av eid samt visning av criipto-gui i popup
 */
@Component({
    selector: 'criipto-autentisering-popup',
    templateUrl: 'criipto-autentisering-popup.html',
    styleUrl: 'criipto-autentisering-popup.scss',
    imports: [
        MatButton,
        MatProgressSpinner,
        TranslateModule,
        InlineMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CriiptoAutentiseringPopupKomponent implements OnDestroy {

    vedValgt = output<CriiptoIdTilbyder>();

    /** klasse som håndtering av criipto respons delegeres til */
    criiptoResponsHandler = input.required<HåndterCriiptoAutentiseringRespons>();
    /** optional - viser alle dersom ikke satt */
    eidmetoder = input<CriiptoIdTilbyder[]>(alleEnuminnslag(CriiptoIdTilbyder));
    /** optional - bruker medium dersom ikke satt */
    tokenvarighet = input<Tokenvarighet>(Tokenvarighet.medium);

    steg = signal<'velgeid' | 'velgsvenskeid' | 'visbankiddialog' | 'callbakmottatt'>('velgeid');
    erNoBankidTilgjengelig = computed(() => this.eidmetoder().includes(CriiptoIdTilbyder.norsk_bankid_web_og_mobil));
    erSeBankidTilgjengelig = computed(() => erOverlappendeLister(this.eidmetoder(), [CriiptoIdTilbyder.svensk_bankid_samedevice, CriiptoIdTilbyder.svensk_bankid_anotherdevice, CriiptoIdTilbyder.svensk_bankid_anotherdeviceqr]));
    popupBlokkert = signal(false);
    noBankidLogoBilde: HttpsUrl;
    seBankidLogoBilde: HttpsUrl;

    private readonly modus = CriiptoGuiModus.popup;
    private valgtTilbyder: CriiptoIdTilbyder | null = null;
    private criiptovindu: WindowProxy | null = null;

    constructor(private avhengigheter: CriiptoAutentiseringPopupKomponentAvhengigheter,
                bildeRessurser: BildeRessurser,
                private ressursAdresser: RessursAdresser,
                private destroyRef: DestroyRef) {
        this.noBankidLogoBilde = bildeRessurser.norskBankidLogoMedTekst;
        this.seBankidLogoBilde = bildeRessurser.svenskBankidLogo;

        // Lytter til melding som sendes av popup som inneholder criipto autentiserings-gui.
        lyttTilCriiptoAutentisertHendelser().pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(event => this.håndterCriiptoCallback(event.origin, event.data));
    }

    velgNoBankid() {
        this.åpneCriiptoPopup(CriiptoIdTilbyder.norsk_bankid_web_og_mobil);
    }

    velgSeBankid() {
        this.steg.set('velgsvenskeid');
    }

    velgSeBankidSamedevice() {
        this.åpneCriiptoPopup(CriiptoIdTilbyder.svensk_bankid_samedevice);
    }

    velgSeBankidAnotherdevice() {
        this.åpneCriiptoPopup(CriiptoIdTilbyder.svensk_bankid_anotherdevice);
    }

    velgSeBankidAnotherdeviceqr() {
        this.åpneCriiptoPopup(CriiptoIdTilbyder.svensk_bankid_anotherdeviceqr);
    }

    tilbakeTilVelgEid() {
        this.steg.set('velgeid');
    }

    ngOnDestroy(): void {
        //ikke la eventuelle åpnede popups henge dersom feks dialog denne komponenten er inkludert i lukkes
        this.lukkCriiptoPopup();
    }

    //private hjelpemetoder

    private åpneCriiptoPopup(tilbyder: CriiptoIdTilbyder) {
        this.steg.set('visbankiddialog');
        this.valgtTilbyder = tilbyder;
        this.vedValgt.emit(tilbyder);
        //skjuler ikke øvrige valg før callback er mottatt

        //for å unngå potensielt problem med popup-blokker i safari lar vi her server redirigere til korrekt url,
        // fremfor å hente url som promise og så åpne vindu når promise resolver - som kan føre til popup-blokkering
        const url = this.ressursAdresser.criiptoAutentiseringUrl(this.modus, tilbyder);
        debug('åpner criipto gui', this.modus, url)

        this.criiptovindu = this.avhengigheter.åpneCriiptoPopup(url); //delegerer til avhengigheter for å kunne mocke ut åpning av vindu i testsammenheng
        if (this.criiptovindu == null) {
            this.steg.set('velgeid');
            this.popupBlokkert.set(true);
        }
    }

    private lukkCriiptoPopup() {
        this.criiptovindu?.close();
        this.criiptovindu = null;
    }

    private async håndterCriiptoCallback(origin: string, data: CriiptoAutentisertMelding) {
        debug(`autentisertIIframeCallback`, origin, data);
        //@todo burde verifisere origin...
        this.lukkCriiptoPopup();

        //håndter eventuell feil
        if (erIkkeTomStreng(data.error)) {
            debug('bruker avbrøt bankiddialogen', data)
            this.avbryt();
            return;
        }

        //håndter autentiseringsdata returnert fra criipto
        this.steg.set('callbakmottatt');
        await this.criiptoResponsHandler().håndterCriiptoAutentiseringRespons(this.somInnloggingsSpesifikasjon(data));
    }

    private somInnloggingsSpesifikasjon(data: CriiptoAutentisertMelding): CriiptoTokenInnloggingsSpesifikasjon {
        return {
            kode: data.code,
            tokenvarighet: this.tokenvarighet(),
            guiModus: this.modus,
            tilbyder: this.valgtTilbyder!,
            styreplanLoginAktivert: false
        }
    }

    private avbryt() {
        this.tilbakeTilVelgEid();
        this.lukkCriiptoPopup();
        this.criiptoResponsHandler().vedCriiptoAutentiseringAvbrutt()
    }

}

export type Feil = {
    type: 'CriiptoAutentiseringBasis.håndterCriiptoRespons';
    e: any;
} | {
    type: 'CriiptoAutentiseringBasis.håndterLederplanRespons.ikkehåndertrespons';
    respons: CriiptoAutentiseringsResponsModell;
}


export interface HåndterCriiptoAutentiseringRespons {
    /**
     * Håndter respons mottatt fra criipto.
     * Ber lederplan server om å håndtere svaret fra criipto og hente autentiseringsinfo fra criipto,
     * logger så bruker inn i lederplan
     */
    håndterCriiptoAutentiseringRespons(spesifikasjon: CriiptoTokenInnloggingsSpesifikasjon): Promise<void>;

    /** Kalles nå bruker avbryter eller data mottatt fra criipto ikke er gyldige */
    vedCriiptoAutentiseringAvbrutt(): void;
}

//Eksempel på HåndterCriiptoAutentiseringRespons implementasjon - ikke fullstendig da denne foreløpig ikke er i bruk..
export abstract class HåndterCriiptoAutentiseringResponsImpl implements HåndterCriiptoAutentiseringRespons {
    constructor(private criiptoOIDCRessurs: CriiptoOIDCRessursClient,
                private autentisering: Brukerautentisering,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser) {
    }

    /**
     * Håndter respons mottatt fra criipto.
     * Ber lederplan server om å håndtere svaret fra criipto og hente autentiseringsinfo fra criipto,
     * logger så bruker inn i lederplan
     */
    async håndterCriiptoAutentiseringRespons(spesifikasjon: CriiptoTokenInnloggingsSpesifikasjon): Promise<void> {
        //vi tar selv ansvar for håndtering, ber lederplan server om å håndtere svaret fra criipto og hente autentiseringsinfo fra criipto
        //logger så bruker inn i lederplan
        try {
            const respons = await this.criiptoOIDCRessurs.autentiser(spesifikasjon).somPromise() as CriiptoAutentiseringsResponsModell;
            this.håndterLederplanRespons(respons);

        } catch (e) {
            this.vedFeil({type: 'CriiptoAutentiseringBasis.håndterCriiptoRespons', e: e});
        }
    }

    /** Kalles nå bruker avbryter eller data mottatt fra criipto ikke er gyldige */
    vedCriiptoAutentiseringAvbrutt(): void {

    };

    //subklasse-kontrakt

    protected vedFeil(feil: Feil): void {
    };

    protected vedBrukerIkkeFunnet(ukjentSubject: SubjectIdIkkeKnyttetTilBrukerModell): void {
    };


    protected vedAutentisert(autentisertOkModell: AutentisertOkModell): void {
    };

    //private hjelpemetoder

    /** Håndterer responsen fra lederplan-server når server har hentet 'openid claims' fra criipto */
    private håndterLederplanRespons(respons: CriiptoAutentiseringsResponsModell) {
        debug('håndterLederplanRespons', respons)

        //noe har ikke gått helt etter planen ...
        switch (respons.lederplanRespons.status) {
            case AutentiseringStatus.autentisert:
                this.loginLederplan(respons);
                return;

            case AutentiseringStatus.ukjentSubject:
                this.vedBrukerIkkeFunnet(respons.lederplanRespons.ukjentSubject);
                return;

            case AutentiseringStatus.ugyldigSpesifikasjon:
                this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Din autentiseringssesjon ser ut til å ha løpt ut på tid, vennligst forsøk på nytt.')), Varighet.klikkBort); //Varighet.sideskift funker ikke - pga sideskift til innloggingssiden
                this.vedCriiptoAutentiseringAvbrutt();
                return;
        }

        debug('håndterLederplanRespons: ikke håndtert respons', respons)
        this.vedFeil({type: 'CriiptoAutentiseringBasis.håndterLederplanRespons.ikkehåndertrespons', respons: respons});
    }

    private loginLederplan(respons: CriiptoAutentiseringsResponsModell) {
        debug('openid autentisert - bruker funnet', respons);
        this.autentisering.brukerAutentisertMotServer(respons.lederplanRespons.autentisertBruker);
        this.vedAutentisert(respons.lederplanRespons.autentisertBruker!);
    }
}

@Injectable()
export class CriiptoAutentiseringPopupKomponentAvhengigheter {

    constructor() {
    }

    åpneCriiptoPopup(url: string): WindowProxy | null {
        return åpneINyttvindu(url, 'criiptogui')
    }
}
